document.addEventListener("DOMContentLoaded", async (): Promise<void> => {
  const game: Game = new Game();
  await game.init();

  initGridSizeDropdown(game);
  initPlayPauseButton(game);
  initResetButton(game);
  initTickTimeoutSlider(game);
});

function initGridSizeDropdown(game: Game): void {
  const inputGridSize: HTMLElement | null = document.getElementById("select-grid-size");
  if (!(inputGridSize instanceof HTMLSelectElement)) {
    console.error(`Failed to get grid size input. Expected HTMLSelectElement, got ${typeof inputGridSize}.`);
    return;
  }

  // listen for every change
  inputGridSize.addEventListener("input", async (event: Event): Promise<void> => {
    const eventTarget: EventTarget | null = event.target;
    if (!(eventTarget instanceof HTMLSelectElement)) {
      console.error(`Failed to get grid size input. Expected HTMLSelectElement, got ${typeof eventTarget}.`);
      return;
    }

    // convert the value to a number
    const gridSize: number = parseInt(eventTarget.value, 10);

    // update the game's grid size
    await game.reset(gridSize);
  });
}

function initPlayPauseButton(game: Game): void {
  const buttonPlayPause: HTMLElement | null = document.getElementById("button-play-pause");
  if (!(buttonPlayPause instanceof HTMLParagraphElement)) {
    console.error(`Failed to get play/pause button. Expected HTMLButtonElement, got ${typeof buttonPlayPause}.`);
    return;
  }

  buttonPlayPause.addEventListener("click", (): void => {
    if (buttonPlayPause.textContent === "▶️") {
      buttonPlayPause.textContent = "⏸️";
    } else {
      buttonPlayPause.textContent = "▶️";
    }
    game.playPause();
  });
}

function initResetButton(game: Game): void {
  const buttonReset: HTMLElement | null = document.getElementById("button-reset");
  if (!(buttonReset instanceof HTMLButtonElement)) {
    console.error(`Failed to get reset button. Expected HTMLButtonElement, got ${typeof buttonReset}.`);
    return;
  }

  buttonReset.addEventListener("click", async (): Promise<void> => {
    await game.reset();
  });
}

function initTickTimeoutSlider(game: Game): void {
  const inputTickTimeout: HTMLElement | null = document.getElementById("input-tick-timeout");
  if (!(inputTickTimeout instanceof HTMLInputElement)) {
    console.error(`Failed to get tick timeout input. Expected HTMLInputElement, got ${typeof inputTickTimeout}.`);
    return;
  }

  // set default game tick timeout
  game.setTickTimeout(inputTickTimeout.value);

  // listen for every change
  inputTickTimeout.addEventListener("input", (event: Event): void => {
    const eventTarget: EventTarget | null = event.target;
    if (!(eventTarget instanceof HTMLInputElement)) {
      console.error(`Failed to get tick timeout input. Expected HTMLInputElement, got ${typeof eventTarget}.`);
      return;
    }

    // update the game's tick timeout
    game.setTickTimeout(eventTarget.value);
  });
}

class Game {
  // canvas
  readonly ASPECT_RATIO: number = 16 / 9;
  readonly WIDTH: number = 3840;
  readonly HEIGHT: number = this.WIDTH / this.ASPECT_RATIO;

  // grid
  GRID_SIZE: number = 64;

  // render loop
  UPDATE_INTERVAL_MILLISECONDS: number = 64;
  step: number = 0;
  intervalId: number | null = null;

  // webgpu
  gpuDevice: GPUDevice | null = null;
  gpuCanvasContext: GPUCanvasContext | null = null;
  cellRenderPipeline: GPURenderPipeline | null = null;
  simulationPipeline: GPUComputePipeline | null = null;
  bindGroups: GPUBindGroup[] | null = null;
  vertexBuffer: GPUBuffer | null = null;
  readonly WORKGROUP_SIZE: number = 8;

  async reset(gridSize: number | null = null): Promise<void> {
    // stop the simulation
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.step = 0;

    // reset all the WebGPU resources
    if (this.gpuDevice !== null) {
      this.gpuDevice.destroy();
      this.gpuDevice = null;
    }
    if (this.gpuCanvasContext !== null) {
      this.gpuCanvasContext.unconfigure();
      this.gpuCanvasContext = null;
    }
    this.cellRenderPipeline = null;
    this.simulationPipeline = null;
    this.bindGroups = null;
    if (this.vertexBuffer !== null) {
      this.vertexBuffer.destroy();
      this.vertexBuffer = null;
    }

    // configure the new grid size, if one was provided
    if (gridSize !== null) {
      this.GRID_SIZE = gridSize;
    }

    // restart the simulation
    await this.init();
  }

  playPause(): void {
    if (this.intervalId === null) {
      this.updateGrid.bind(this);
      this.intervalId = setInterval(this.updateGrid.bind(this), this.UPDATE_INTERVAL_MILLISECONDS);
    } else {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  async init(): Promise<void> {
    //
    // WEBGPU
    //

    // make sure the browser has WebGPU support
    if (!navigator.gpu) {
      alert("WebGPU not supported on this browser. Please try Google Chrome!");
      throw Error("WebGPU not supported on this browser. Please try Google Chrome!");
    }

    // get access to "WebGPU's representation of a specific piece of GPU hardware in the user's computer"
    const gpuAdapter: GPUAdapter | null = await navigator.gpu.requestAdapter();
    if (gpuAdapter === null) {
      alert("WebGPU not supported on this browser. Please try Google Chrome!");
      throw Error("Failed to get GPU Adapter. WebGPU not supported on this browser. Please try Google Chrome!");
    }

    // get access to the "main interface through which most interaction with the GPU happens"
    this.gpuDevice = await gpuAdapter.requestDevice();

    // set up the canvas to use the new GPUDevice
    this.gpuCanvasContext = this.initCanvas();
    const gpuTextureFormat: GPUTextureFormat = navigator.gpu.getPreferredCanvasFormat();
    this.gpuCanvasContext.configure({
      device: this.gpuDevice,
      format: gpuTextureFormat,
      width: this.WIDTH,
      height: this.HEIGHT,
    });

    //
    //  CELL
    //

    // create an array/buffer to represent the state of each cell in the grid
    const cellStateArray: Uint32Array = new Uint32Array(this.GRID_SIZE * this.GRID_SIZE);
    const cellStateStorage: GPUBuffer[] = [
      this.gpuDevice.createBuffer({
        label: "Cell State 1",
        size: cellStateArray.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      this.gpuDevice.createBuffer({
        label: "Cell State 2",
        size: cellStateArray.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
    ];

    // initialize the first storage buffer with random cell states
    // (prefer more "living" cells than "dead" cells)
    for (let i: number = 0; i < cellStateArray.length; i++) {
      cellStateArray[i] = Math.random() > 0.6 ? 1 : 0;
    }
    this.gpuDevice.queue.writeBuffer(cellStateStorage[0], 0, cellStateArray);

    // create a uniform buffer to hold the grid size
    // a "uniform" is a buffer value that is the same for every invocation of the vertex shader
    const uniformArray: Float32Array = new Float32Array([this.GRID_SIZE, this.GRID_SIZE]);
    const uniformBuffer: GPUBuffer = this.gpuDevice.createBuffer({
      label: "Grid Uniforms",
      size: uniformArray.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.gpuDevice.queue.writeBuffer(uniformBuffer, 0, uniformArray);

    // create a contiguous block of memory to hold the vertices of a square
    // deno-fmt-ignore
    const vertices: Float32Array = new Float32Array([
      // X, Y

      // square
      // 1
      -0.8, -0.8,
      0.8, -0.8,
      0.8, 0.8,
      // 2
      -0.8, -0.8,
      0.8, 0.8,
      -0.8, 0.8,

      // // star
      // // 1
      // 0.0, 0.0,
      // 0.0, 0.9,
      // -0.4, 0.0,
      // // 2
      // 0.0, 0.0,
      // 0.4, 0.0,
      // 0.0, 0.9,
      // // 3
      // 0.0, 0.0,
      // -0.9, 0.0,
      // 0.0, 0.4,
      // // 4
      // 0.0, 0.0,
      // 0.9, 0.0,
      // 0.0, 0.4,
      // // 5
      // 0.0, 0.0,
      // -0.9, 0.0,
      // 0.0, -0.4,
      // // 6
      // 0.0, 0.0,
      // 0.9, 0.0,
      // 0.0, -0.4,
      // // 7
      // -0.4, 0.0,
      // -0.5, -0.9,
      // 0.0, -0.4,
      // // 8
      // 0.4, 0.0,
      // 0.5, -0.9,
      // 0.0, -0.4,
    ].map((value: number, _index: number): number => {
      // scale the vertices to the aspect ratio
      // if (index % 2 === 0) {
      //   return value / this.ASPECT_RATIO;
      // }
      return value;
    }));
    this.vertexBuffer = this.gpuDevice.createBuffer({
      label: "Cell vertices",
      size: vertices.byteLength,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST, /* allows copying data into it */
    });
    this.gpuDevice.queue.writeBuffer(this.vertexBuffer, 0, vertices);

    // describe the structure of the vertex data to the GPU
    const vertexBufferLayout: {
      arrayStride: number;
      attributes: [{ format: GPUVertexFormat; offset: number; shaderLocation: number }];
    } = {
      arrayStride: 4 /* bytes */ * 2, /* positions: X, Y */
      attributes: [{
        format: "float32x2", // X, Y
        offset: 0, // how many bytes into the vertex this particular attribute starts
        shaderLocation: 0, // arbitrary unique number between 0-15; links attribute to particular vertex shader input
      }],
    };

    // define vertex and fragment shaders
    const cellShaderModule: GPUShaderModule = this.gpuDevice.createShaderModule({
      label: "Cell shader",
      code: `
      @group(0) @binding(0) var<uniform> gridSize: vec2f;
      // TODO - figure out how to store 32 cells state in a single u32
      @group(0) @binding(1) var<storage> cellState: array<u32>;

      struct VertexInput {
        @location(0) position: vec2f,
        @builtin(instance_index) instance: u32
      }

      struct VertexOutput {
        @builtin(position) position: vec4f,
        @location(0) cell: vec2f,
      }

      @vertex
      fn vertexMain(input: VertexInput) -> VertexOutput {
        let instanceIndex = f32(input.instance);
        let cell = vec2f(
          instanceIndex % gridSize.x,
          floor(instanceIndex / gridSize.x)
        );
        let state = f32(cellState[input.instance]);
        let cellOffset = cell / gridSize * 2;
        let gridPosition = ((input.position + 1) / gridSize - 1 + cellOffset) * state;

        var output: VertexOutput;
        output.position = vec4f(gridPosition, 0, 1); // (X, Y), Z, W
        output.cell = cell;
        return output;
      }

      struct FragmentInput {
        @location(0) cell: vec2f,
      }

      @fragment
      fn fragmentMain(input: FragmentInput) -> @location(0) vec4f {
        let color = input.cell / gridSize;
        return vec4f(color, 1-color.x, 1); // (R, G), B, A
      }
      `,
    });

    // define compute shader
    const simulationShaderModule: GPUShaderModule = this.gpuDevice.createShaderModule({
      label: "Simulation shader",
      code: `
      @group(0) @binding(0) var<uniform> gridSize: vec2f;

      @group(0) @binding(1) var<storage> cellStateIn: array<u32>;
      @group(0) @binding(2) var<storage, read_write> cellStateOut: array<u32>;

      fn cellIndex(cell: vec2u) -> u32 {
        return (cell.y % u32(gridSize.y)) * u32(gridSize.x) + (cell.x % u32(gridSize.x));
      }

      fn cellActive(x: u32, y: u32) -> u32 {
        return cellStateIn[cellIndex(vec2u(x, y))];
      }

      @compute
      @workgroup_size(${this.WORKGROUP_SIZE}, ${this.WORKGROUP_SIZE})
      fn computeMain(@builtin(global_invocation_id) cell: vec3u) {
        let activeNeighbors: u32 =  cellActive(cell.x+1, cell.y+1) +
                                    cellActive(cell.x+1, cell.y) +
                                    cellActive(cell.x+1, cell.y-1) +
                                    cellActive(cell.x, cell.y+1) +
                                    cellActive(cell.x, cell.y-1) +
                                    cellActive(cell.x-1, cell.y+1) +
                                    cellActive(cell.x-1, cell.y) +
                                    cellActive(cell.x-1, cell.y-1);

        let index = cellIndex(cell.xy);

        switch activeNeighbors {
          // cells that have 2 neighbors stay dead/alive
          case 2: {
            cellStateOut[index] = cellStateIn[index];
          }
          // cells that have 3 neighbors come alive
          case 3: {
            cellStateOut[index] = 1;
          }
          // all other cells die
          default: {
            cellStateOut[index] = 0;
          }
        }
      }
      `,
    });

    // create a bind group layout, which "describes the layout of the bind groups that a pipeline expects"
    const bindGroupLayout: GPUBindGroupLayout = this.gpuDevice.createBindGroupLayout({
      label: "Cell Bind Group Layout",
      entries: [{
        binding: 0,
        visibility: GPUShaderStage.VERTEX | GPUShaderStage.COMPUTE | GPUShaderStage.FRAGMENT,
        buffer: {
          type: "uniform", // grid uniform buffer
        },
      }, {
        binding: 1,
        visibility: GPUShaderStage.VERTEX | GPUShaderStage.COMPUTE,
        buffer: {
          type: "read-only-storage", // cell state input buffer
        },
      }, {
        binding: 2,
        visibility: GPUShaderStage.COMPUTE,
        buffer: {
          type: "storage", // cell state output buffer
        },
      }],
    });

    // create a pipeline layout, which "describes the layout of the bind groups that a pipeline expects"
    const pipelineLayout: GPUPipelineLayout = this.gpuDevice.createPipelineLayout({
      label: "Cell pipeline layout",
      bindGroupLayouts: [bindGroupLayout],
    });

    // create a render pipeline, which "controls how geometry is drawn, which shaders are used,
    // how vertex buffer data is interpreted, which kind of geometry is rendered (points, lines, triangles), etc"
    this.cellRenderPipeline = this.gpuDevice.createRenderPipeline({
      label: "Cell pipeline",
      layout: pipelineLayout,
      vertex: {
        module: cellShaderModule,
        entryPoint: "vertexMain",
        buffers: [vertexBufferLayout],
      },
      fragment: {
        module: cellShaderModule,
        entryPoint: "fragmentMain",
        targets: [{
          format: gpuTextureFormat,
        }],
      },
    });

    // create a compute pipeline, which "controls how compute operations are executed"
    this.simulationPipeline = this.gpuDevice.createComputePipeline({
      label: "Simulation pipeline",
      layout: pipelineLayout,
      compute: {
        module: simulationShaderModule,
        entryPoint: "computeMain",
      },
    });

    // create a bind group, which is a "collection of resources that you want to make accessible to a shader"
    this.bindGroups = [
      this.gpuDevice.createBindGroup({
        label: "Cell renderer bind group 1",
        layout: bindGroupLayout,
        entries: [{
          binding: 0,
          resource: {
            buffer: uniformBuffer,
          },
        }, {
          binding: 1,
          resource: {
            buffer: cellStateStorage[0],
          },
        }, {
          binding: 2,
          resource: {
            buffer: cellStateStorage[1],
          },
        }],
      }),
      this.gpuDevice.createBindGroup({
        label: "Cell renderer bind group 2",
        layout: bindGroupLayout,
        entries: [{
          binding: 0,
          resource: {
            buffer: uniformBuffer,
          },
        }, {
          binding: 1,
          resource: {
            buffer: cellStateStorage[1],
          },
        }, {
          binding: 2,
          resource: {
            buffer: cellStateStorage[0],
          },
        }],
      }),
    ];

    // schedule the simulation to run
    this.updateGrid.bind(this);
    this.intervalId = setInterval(this.updateGrid.bind(this), this.UPDATE_INTERVAL_MILLISECONDS);
  }

  updateGrid(): void {
    if (this.gpuDevice === null) {
      throw Error("GPU Device is null.");
    }
    if (this.gpuCanvasContext === null) {
      throw Error("GPU Canvas Context is null.");
    }
    if (this.cellRenderPipeline === null) {
      throw Error("Cell Render Pipeline is null.");
    }
    if (this.simulationPipeline === null) {
      throw Error("Simulation Pipeline is null.");
    }
    if (this.bindGroups === null) {
      throw Error("Bind Groups is null.");
    }
    if (this.vertexBuffer === null) {
      throw Error("Vertex Buffer is null.");
    }

    // create an encoder, which "provides a way to record commands for the GPU to execute"
    const gpuCommandEncoder: GPUCommandEncoder = this.gpuDevice.createCommandEncoder();

    // create a compute pass, which is "where all compute operations in WebGPU happen"
    const gpuComputePassEncoder: GPUComputePassEncoder = gpuCommandEncoder.beginComputePass();
    gpuComputePassEncoder.setPipeline(this.simulationPipeline);
    gpuComputePassEncoder.setBindGroup(0, this.bindGroups[this.step % 2]);

    // set the workgroup size
    const workgroupCount: number = Math.ceil(this.GRID_SIZE / this.WORKGROUP_SIZE);
    gpuComputePassEncoder.dispatchWorkgroups(workgroupCount, workgroupCount, 1);

    // end the compute pass
    gpuComputePassEncoder.end();

    // update the step between passes
    // (so that the output buffer of the compute pipeline becomes the input buffer for the render pipeline)
    this.step++;
    if (this.step === 2) {
      this.step = 0;
    }

    // creat a render pass, which is "where all drawing operations in WebGPU happen";
    const gpuRenderPassEncoder: GPURenderPassEncoder = gpuCommandEncoder.beginRenderPass({
      colorAttachments: [
        {
          view: this.gpuCanvasContext.getCurrentTexture().createView(),
          loadOp: "clear", // indicates "texture should be cleared when render pass starts"
          clearValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 }, // change clear color (values must be between 0.0-1.0)
          storeOp: "store", // indicates "save results of all drawing done during render pass to the texture"
        },
      ],
    });
    gpuRenderPassEncoder.setPipeline(this.cellRenderPipeline);
    gpuRenderPassEncoder.setBindGroup(0, this.bindGroups[this.step % 2]); // slot 0 is the first index of GPURenderPipeline.layout.bindGroupLayouts

    // draw the square
    gpuRenderPassEncoder.setVertexBuffer(0, this.vertexBuffer); // slot 0 is the first index of GPURenderPipeline.vertex.buffers
    gpuRenderPassEncoder.draw(
      this.vertexBuffer.size / 4 / 2, // 4 bytes per float, 2 floats per vertex
      this.GRID_SIZE * this.GRID_SIZE,
    );

    // end the render pass
    gpuRenderPassEncoder.end();

    // get access to a command buffer, which is an "opaque handle to the recorded commands"
    // submit the command buffer to the GPU Device
    // (once a command buffer is submitted, it cannot be used again)
    this.gpuDevice.queue.submit([gpuCommandEncoder.finish()]);
  }

  initCanvas(): GPUCanvasContext {
    const canvas: HTMLElement | null = document.getElementById("webgpu-canvas");
    if (canvas === null) {
      throw Error("Failed to get canvas.");
    }
    if (!(canvas instanceof HTMLCanvasElement)) {
      throw Error("Canvas was unexpectedly not an HTMLCanvasElement.");
    }

    // handle hdpi
    const devicePixelRatio: number = globalThis.devicePixelRatio;
    canvas.width = this.WIDTH * devicePixelRatio;
    canvas.height = this.HEIGHT * devicePixelRatio;

    const gpuCanvasContext: GPUCanvasContext = canvas.getContext("webgpu");
    if (gpuCanvasContext === null) {
      alert("WebGPU not supported on this browser. Please try Google Chrome!");
      throw Error("Failed to get GPU Canvas Context. WebGPU not supported on this browser. Please try Google Chrome!");
    }
    return gpuCanvasContext;
  }

  setTickTimeout(valueString: string): void {
    // convert from string to number
    let valueNumber: number = parseInt(valueString, 10);

    // enforce bounds
    if (valueNumber < 1) {
      valueNumber = 1;
    }
    if (valueNumber > 5) {
      valueNumber = 5;
    }

    // convert value to milliseconds
    switch (valueNumber) {
      case 1:
        valueNumber = 4;
        break;
      case 2:
        valueNumber = 16;
        break;
      case 3:
        valueNumber = 64;
        break;
      case 4:
        valueNumber = 256;
        break;
      case 5:
        valueNumber = 1024;
        break;
    }

    // update the game's tick timeout
    this.UPDATE_INTERVAL_MILLISECONDS = valueNumber;

    // clear the current interval
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
    }

    // schedule the simulation to run
    this.updateGrid.bind(this);
    this.intervalId = setInterval(this.updateGrid.bind(this), this.UPDATE_INTERVAL_MILLISECONDS);
  }
}
